/**
 * 📜 CONDITIONS GÉNÉRALES D'UTILISATION - Heartbeats
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, FileText, Users, Shield, AlertTriangle, CreditCard, Ban } from 'lucide-react';

const HeartbeatsCGU = ({ onBack }) => {
  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#030014] via-[#0F0529] to-[#030014] text-white" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#030014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={onBack || (() => window.history.back())}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-500" fill="currentColor" />
            <h1 className="text-xl font-bold">Conditions Générales d'Utilisation</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Introduction */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-pink-400 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Préambule
            </h2>
            <div className="space-y-3 text-white/80">
              <p>
                Bienvenue sur Heartbeats ! En utilisant notre application de rencontres, 
                vous acceptez les présentes Conditions Générales d'Utilisation (CGU).
              </p>
              <p>
                Heartbeats est une plateforme de mise en relation permettant à des personnes 
                majeures de se rencontrer dans un cadre respectueux et sécurisé.
              </p>
            </div>
          </section>

          {/* Conditions d'accès */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-purple-400 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Article 1 - Conditions d'accès
            </h2>
            <div className="space-y-3 text-white/80">
              <p><strong>1.1 Âge minimum</strong></p>
              <p>
                L'utilisation de Heartbeats est strictement réservée aux personnes 
                âgées de <strong>18 ans et plus</strong>. En créant un compte, vous certifiez 
                être majeur(e) selon la législation de votre pays de résidence.
              </p>
              <p><strong>1.2 Inscription</strong></p>
              <p>
                Pour utiliser Heartbeats, vous devez créer un compte avec une adresse 
                email valide et un mot de passe sécurisé. Vous êtes responsable de la 
                confidentialité de vos identifiants.
              </p>
              <p><strong>1.3 Véracité des informations</strong></p>
              <p>
                Vous vous engagez à fournir des informations exactes et à jour. 
                L'utilisation de fausses informations ou de photos trompeuses est interdite.
              </p>
            </div>
          </section>

          {/* Utilisation du service */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5" />
              Article 2 - Utilisation du service
            </h2>
            <div className="space-y-3 text-white/80">
              <p><strong>2.1 Objet du service</strong></p>
              <p>
                Heartbeats permet de découvrir des profils, d'échanger des messages, 
                et de créer des connexions avec d'autres utilisateurs.
              </p>
              <p><strong>2.2 Fonctionnalités</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Création de profil avec photos et description</li>
                <li>Système de "like" et de matching</li>
                <li>Messagerie privée entre utilisateurs matchés</li>
                <li>Filtres de recherche par localisation, âge, centres d'intérêt</li>
                <li>Fonctionnalités Premium optionnelles</li>
              </ul>
              <p><strong>2.3 Couronnes et achats in-app</strong></p>
              <p>
                Les Couronnes sont une monnaie virtuelle permettant d'accéder à des 
                fonctionnalités premium. Elles ne sont ni remboursables ni échangeables 
                contre de l'argent réel.
              </p>
            </div>
          </section>

          {/* Comportement */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-amber-400 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Article 3 - Règles de comportement
            </h2>
            <div className="space-y-3 text-white/80">
              <p><strong>3.1 Respect mutuel</strong></p>
              <p>
                Vous vous engagez à traiter les autres utilisateurs avec respect et courtoisie.
                Tout comportement irrespectueux, harcèlement ou discrimination est strictement interdit.
              </p>
              <p><strong>3.2 Contenus interdits</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Contenus à caractère pornographique ou sexuellement explicites</li>
                <li>Propos haineux, racistes, homophobes ou discriminatoires</li>
                <li>Incitation à la violence ou activités illégales</li>
                <li>Spam, publicité non autorisée, arnaques</li>
                <li>Usurpation d'identité</li>
              </ul>
              <p><strong>3.3 Signalement</strong></p>
              <p>
                Vous pouvez signaler tout comportement inapproprié via la fonction de 
                signalement intégrée à l'application.
              </p>
            </div>
          </section>

          {/* Interdictions */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-red-400 mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Article 4 - Interdictions
            </h2>
            <div className="space-y-3 text-white/80">
              <p>Il est strictement interdit de :</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Utiliser Heartbeats à des fins commerciales ou promotionnelles</li>
                <li>Collecter des données personnelles d'autres utilisateurs</li>
                <li>Tenter de contourner les mesures de sécurité</li>
                <li>Créer plusieurs comptes</li>
                <li>Vendre ou transférer son compte</li>
                <li>Utiliser des bots ou scripts automatisés</li>
                <li>Solliciter de l'argent auprès d'autres utilisateurs</li>
              </ul>
            </div>
          </section>

          {/* Abonnements */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-green-400 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Article 5 - Abonnements et paiements
            </h2>
            <div className="space-y-3 text-white/80">
              <p><strong>5.1 Abonnement Premium</strong></p>
              <p>
                Heartbeats propose des abonnements Premium offrant des fonctionnalités 
                avancées. Les tarifs sont affichés avant tout achat.
              </p>
              <p><strong>5.2 Renouvellement</strong></p>
              <p>
                Les abonnements se renouvellent automatiquement sauf annulation 
                effectuée au moins 24h avant la fin de la période en cours.
              </p>
              <p><strong>5.3 Remboursement</strong></p>
              <p>
                Les achats de Couronnes et abonnements Premium ne sont pas remboursables, 
                sauf disposition légale contraire dans votre pays de résidence.
              </p>
              <p><strong>5.4 Droit de rétractation</strong></p>
              <p>
                Conformément à l'article L221-28 du Code de la consommation, le droit 
                de rétractation ne s'applique pas aux contenus numériques fournis 
                immédiatement après l'achat.
              </p>
            </div>
          </section>

          {/* Responsabilité */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-orange-400 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Article 6 - Responsabilité
            </h2>
            <div className="space-y-3 text-white/80">
              <p><strong>6.1 Rencontres</strong></p>
              <p>
                Heartbeats est un service de mise en relation. Nous ne sommes pas 
                responsables des rencontres entre utilisateurs ni de leurs conséquences.
              </p>
              <p><strong>6.2 Conseils de sécurité</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Rencontrez-vous dans des lieux publics</li>
                <li>Prévenez un proche de vos rendez-vous</li>
                <li>Ne partagez pas d'informations financières</li>
                <li>Signalez tout comportement suspect</li>
              </ul>
              <p><strong>6.3 Disponibilité du service</strong></p>
              <p>
                Nous nous efforçons d'assurer la disponibilité du service 24/7, mais 
                ne garantissons pas une disponibilité ininterrompue.
              </p>
            </div>
          </section>

          {/* Sanctions */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-red-400 mb-4">Article 7 - Sanctions</h2>
            <div className="space-y-3 text-white/80">
              <p>
                En cas de violation des présentes CGU, Heartbeats se réserve le droit de :
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Avertir l'utilisateur</li>
                <li>Suspendre temporairement le compte</li>
                <li>Supprimer définitivement le compte sans remboursement</li>
                <li>Engager des poursuites judiciaires si nécessaire</li>
              </ul>
            </div>
          </section>

          {/* Modification */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-cyan-400 mb-4">Article 8 - Modification des CGU</h2>
            <div className="space-y-3 text-white/80">
              <p>
                Heartbeats se réserve le droit de modifier les présentes CGU à tout moment. 
                Les utilisateurs seront informés des modifications importantes par email 
                ou notification dans l'application.
              </p>
              <p>
                La continuation de l'utilisation du service après modification vaut 
                acceptation des nouvelles CGU.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-white/5 rounded-2xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-pink-400 mb-4">Article 9 - Contact</h2>
            <div className="space-y-3 text-white/80">
              <p>Pour toute question relative aux présentes CGU :</p>
              <p>📧 Email : <strong>legal@heartbeats.fr</strong></p>
              <p>📍 Adresse : 141 Rue Arthur Lamendin, 62980 Vermelles, France</p>
            </div>
          </section>

          {/* Date */}
          <p className="text-center text-white/40 text-sm">
            Dernière mise à jour : Janvier 2026
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default HeartbeatsCGU;
